import React, { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';
import { FormSection } from '../FormSection/FormSection';
import { useAddressLookupQuery } from '@/src/api/use-address-lookup';
import { LoadingContent } from '@ovotech/energy-cx/dist/shared/core/components';
import { AddressField } from '../AddressField/AddressField';
import { Eligibility } from '../Eligibility/Eligibility';
import {
  CTAButton,
  SecondaryCTAButton,
  Margin,
  Checkbox,
  Stack,
} from '@ovotech/element-v4';
import { TextField } from '@ovotech/element-v5';
import {
  invalidPostcodeError,
  isValidPostcode,
  requiredFieldError,
} from '../../utils/validationUtils';
import { GbgAddressErrors, GbgAddressValues } from '@/src/types/MoveOut';
import { AnalyticsClick } from '@ovotech/ui-tools';
import {
  HOME_MOVES_ADDRESS_OUTSIDE_UK,
  HOME_MOVES_NO_UK_ADDRESS,
  HOME_MOVES_FIND_ADDRESS,
  HOME_MOVES_CANT_FIND_ADDRESS,
} from '@/src/constants/analytics-subjects';
import styles from './GbgAddressSection.module.css';
import { FormValues } from '../MovingHomeForm/useMovingHomeFormState';
import { isMovingDateComplete } from '../../utils/movingDateValuesConversion';

type GbgAddressSectionProps = {
  values: FormValues;
  errors: GbgAddressErrors;
  onValuesChange: (values: GbgAddressValues) => void;
  onErrorsChange: (errors: GbgAddressErrors) => void;
  onCantFindAddress: () => void;
  onGetQuote: ({ isCot }: { isCot: boolean }) => void;
  onEndContract: () => void;
  onNoUkAddress: () => void;
};

export const GbgAddressSection = ({
  values,
  errors,
  onValuesChange,
  onErrorsChange,
  onCantFindAddress,
  onGetQuote,
  onEndContract,
  onNoUkAddress,
}: GbgAddressSectionProps) => {
  const [lookupPostcode, setLookupPostcode] = useState<string>(
    values.gbgAddress.postCode,
  );

  const [eligibilityPostcode, setEligibilityPostcode] = useState(
    values.gbgAddress.postCode,
  );

  const addressLookupResult = useAddressLookupQuery(lookupPostcode || '', {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    cacheTime: 0,
  });

  const showAddressField = addressLookupResult.status !== 'no-data';

  const eligibilityEnabled =
    Boolean(values.gbgAddress.address) &&
    isMovingDateComplete(values.movingDate);

  const addresses =
    addressLookupResult.status === 'data'
      ? addressLookupResult.data
      : undefined;

  const { gbgAddress } = values;

  useEffect(() => {
    if (gbgAddress.address === undefined) {
      return;
    }

    const addressExists = addresses?.some(
      address =>
        address.propertySearchId === gbgAddress.address?.propertySearchId,
    );
    if (!addressExists) {
      onValuesChange({ ...gbgAddress, address: undefined });
    }
  }, [gbgAddress, addresses, onValuesChange]);

  const lookupAddress = async () => {
    const { postCode } = gbgAddress;

    if (!postCode) {
      onErrorsChange({ ...errors, postCode: requiredFieldError });
    } else if (!isValidPostcode(postCode)) {
      onErrorsChange({ ...errors, postCode: invalidPostcodeError });
    } else {
      onErrorsChange({ ...errors, postCode: undefined });
      setLookupPostcode(postCode);
    }
  };

  return (
    <FormSection
      id="moving-home-gbg-address-section"
      className={styles.formSection}
      title="What's your new address?"
      description="This is where we'll send your final bill. We'll also check if we can power your new home."
    >
      <Margin top={[4, 6, 6]}>
        <Stack spaceBetween={[4, 5, 6]}>
          {gbgAddress.addressOutsideUk && (
            <AnalyticsClick name={HOME_MOVES_NO_UK_ADDRESS}>
              <CTAButton onClick={onNoUkAddress}>Next</CTAButton>
            </AnalyticsClick>
          )}

          {!gbgAddress.addressOutsideUk && (
            <>
              <div className={styles.postcodeGroup}>
                <div className={styles.container}>
                  <TextField
                    id="moving-home-postcode"
                    label="Postcode"
                    error={errors.postCode}
                    fullWidth={true}
                    value={gbgAddress.postCode}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      onValuesChange({
                        ...gbgAddress,
                        postCode: e.target.value.toUpperCase(),
                      });
                    }}
                    onBlur={lookupAddress}
                    onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        lookupAddress();
                      }
                    }}
                  />
                </div>
                <AnalyticsClick name={HOME_MOVES_FIND_ADDRESS}>
                  <CTAButton
                    className={styles.addressLookupButton}
                    onClick={() => {
                      lookupAddress();
                    }}
                  >
                    <LoadingContent
                      loading={
                        !gbgAddress.addressOutsideUk &&
                        addressLookupResult.isFetching
                      }
                    >
                      Find address
                    </LoadingContent>
                  </CTAButton>
                </AnalyticsClick>
              </div>

              <Stack spaceBetween={[4, 5, 6]} className={styles.container}>
                {showAddressField && (
                  <AddressField
                    id="moving-home-address"
                    value={gbgAddress.address}
                    error={errors.address}
                    addresses={addresses}
                    onValueChange={address => {
                      onValuesChange({ ...gbgAddress, address });
                      setEligibilityPostcode(gbgAddress.postCode);
                    }}
                  />
                )}
                <AnalyticsClick
                  name={HOME_MOVES_CANT_FIND_ADDRESS}
                  properties={{
                    gbgReturnedAddresses: !!addresses?.length,
                  }}
                >
                  <SecondaryCTAButton
                    className={styles.manualAddressButton}
                    onClick={onCantFindAddress}
                  >
                    I can't find my address
                  </SecondaryCTAButton>
                </AnalyticsClick>
              </Stack>

              <AnalyticsClick
                name={HOME_MOVES_ADDRESS_OUTSIDE_UK}
                properties={{
                  checked: gbgAddress.addressOutsideUk,
                }}
              >
                <Checkbox
                  id="moving-home-address-outside-uk"
                  label="My new address is outside the UK"
                  checked={gbgAddress.addressOutsideUk}
                  onChange={e => {
                    onValuesChange({
                      ...gbgAddress,
                      addressOutsideUk: e.target.checked,
                    });
                  }}
                />
              </AnalyticsClick>

              {eligibilityEnabled && (
                <Eligibility
                  movingDateValues={values.movingDate}
                  postCode={eligibilityPostcode}
                  propertySearchId={gbgAddress.address?.propertySearchId || ''}
                  onGetQuoteClick={onGetQuote}
                  onEndContractClick={onEndContract}
                />
              )}
            </>
          )}
        </Stack>
      </Margin>
    </FormSection>
  );
};
